/**
 * ResonantDrop — co-op ability unlocked after Level 1 (Elder Leo).
 *
 * Mechanic: Basil performs a ground-slam while Minna purrs.
 * When both cats are within RESONANT_DROP_RANGE and the ability
 * is triggered, a shockwave radiates outward from the midpoint,
 * shattering any RustedBlockage in range.
 *
 * Visual: expanding torus rings (brass/emerald/copper) + particle burst.
 *
 * Reduced-motion support:
 *   - particleDensityFactor() scales manualEmitCount and capacity at
 *     activate() time.
 *   - largeVisualScale() shrinks shockwaveMaxRadius so the ring reach is
 *     reduced — the ability effect is unchanged, only the visual is smaller.
 *   - ACCESSIBILITY_CHANGED re-tunes manualEmitCount live (emitRate is 0
 *     for this burst, so only manualEmitCount matters).
 *   - Particle capacity is fixed at ParticleSystem construction time by
 *     Babylon.js — only the NEXT activation will reflect a capacity change.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Ability } from "../../systems/AbilitySystem";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { SCRAPYARD, COLORS } from "../../config/GameConfig";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { particleDensityFactor, largeVisualScale } from "../../systems/particleScaling";
import { a11yColor3, a11yColor4 } from "../../systems/abilityColorHelpers";
import { createSoftParticleTexture } from "../../core/particleTextures";

/** Level-local artwork; gameplay radius, epicenter and duration stay fixed. */
export interface ResonantDropVisualConfig {
  ringPlane?: "upright" | "ground";
  ringHeightOffset?: number;
  ringThicknessScale?: number;
  ringOpacity?: number;
  softParticles?: boolean;
  particleDensityScale?: number;
}

export class ResonantDrop implements Ability {
  readonly id = "resonantDrop";
  readonly name = "Resonant Drop";
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = SCRAPYARD.PUZZLE.RESONANT_DROP_COOLDOWN;

  private scene: Scene;
  private basilMesh: Mesh;
  private minnaMesh: Mesh;

  private shockwaveRadius = 0;
  private readonly shockwaveMaxRadiusBase = SCRAPYARD.PUZZLE.RESONANT_DROP_RADIUS;
  /** Effective max radius for the current activation — reduced when reducedMotion is on. */
  private shockwaveMaxRadius: number;
  private readonly shockwaveDuration = 0.8; // seconds
  private shockwaveTimer = 0;

  /** Base particle capacity/manualEmitCount before reduced-motion scaling. */
  private static readonly BASE_CAPACITY = 60;
  private static readonly BASE_MANUAL_EMIT = 60;

  private ringMeshes: Mesh[] = [];
  private particles: ParticleSystem | null = null;

  /** Unsubscribe function for ACCESSIBILITY_CHANGED listener. */
  private _a11yUnsub: (() => void) | null = null;

  constructor(scene: Scene, basilMesh: Mesh, minnaMesh: Mesh,
    private readonly visualConfig: ResonantDropVisualConfig = {}) {
    this.scene = scene;
    this.basilMesh = basilMesh;
    this.minnaMesh = minnaMesh;
    this.shockwaveMaxRadius = this.shockwaveMaxRadiusBase;

    // Live re-tune: update manualEmitCount whenever reduced-motion changes.
    // emitRate is 0 for this one-shot burst ability, so we tune manualEmitCount.
    // NOTE: capacity is fixed at ParticleSystem construction time — only the
    // NEXT activation rescales capacity.
    this._a11yUnsub = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, (settings) => {
      if (this.particles) {
        const factor = particleDensityFactor(settings.reducedMotion);
        this.particles.manualEmitCount = Math.max(
          1,
          Math.ceil(ResonantDrop.BASE_MANUAL_EMIT * factor * (this.visualConfig.particleDensityScale ?? 1)),
        );
      }
    });
  }

  canActivate(): boolean {
    if (this.isActive || this.cooldownRemaining > 0) return false;

    const dist = Vector3.Distance(
      this.basilMesh.position,
      this.minnaMesh.position,
    );
    return dist <= SCRAPYARD.PUZZLE.RESONANT_DROP_RANGE;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.shockwaveRadius = 0;
    this.shockwaveTimer = 0;

    const reduced = AccessibilitySystem.getInstance().state.reducedMotion;
    const factor = particleDensityFactor(reduced);
    const visualScale = largeVisualScale(reduced);
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);

    // Reduced-motion: shrink the ring reach (visual only — the gameplay radius
    // emitted via RESONANT_DROP_TRIGGERED still uses the full base radius so
    // blockage detection is not affected by accessibility mode).
    this.shockwaveMaxRadius = this.shockwaveMaxRadiusBase * visualScale;

    const epicenter = Vector3.Center(
      this.basilMesh.position,
      this.minnaMesh.position,
    );

    // Create three expanding ring meshes at different scales
    const ringColors = [
      a11yColor3('BRASS', COLORS.BRASS, rc),
      a11yColor3('EMERALD', COLORS.EMERALD, rc),
      a11yColor3('COPPER', COLORS.COPPER, rc),
    ];
    this.ringMeshes = [];
    for (let i = 0; i < 3; i++) {
      const ring = MeshBuilder.CreateTorus(
        `resonantRing_${i}`,
        { diameter: 0.5, thickness: (0.06 - i * 0.015) * (this.visualConfig.ringThicknessScale ?? 1), tessellation: 32 },
        this.scene,
      );
      ring.position = epicenter.clone();
      ring.position.y += this.visualConfig.ringHeightOffset ?? 0;
      // Babylon toruses lie in XZ by default. Preserve the old upright
      // treatment unless a level requests a wave along its walking surface.
      ring.rotation.x = this.visualConfig.ringPlane === "ground" ? 0 : Math.PI / 2;

      const mat = new StandardMaterial(`ringMat_${i}`, this.scene);
      mat.diffuseColor = ringColors[i];
      mat.emissiveColor = ringColors[i].scale(0.6);
      mat.alpha = this.visualConfig.ringOpacity ?? 0.8;
      mat.backFaceCulling = false;
      ring.material = mat;
      this.ringMeshes.push(ring);
    }

    // Particle burst — capacity and manualEmitCount scaled by reducedMotion factor.
    // NOTE: capacity is fixed at construction time; only the NEXT activation
    // rescales capacity. manualEmitCount can be updated live via ACCESSIBILITY_CHANGED.
    const density = this.visualConfig.particleDensityScale ?? 1;
    const capacity = Math.max(1, Math.ceil(ResonantDrop.BASE_CAPACITY * factor * density));
    const manualCount = Math.max(1, Math.ceil(ResonantDrop.BASE_MANUAL_EMIT * factor * density));
    this.particles = new ParticleSystem("resonantParticles", capacity, this.scene);
    if (this.visualConfig.softParticles) {
      this.particles.particleTexture = createSoftParticleTexture(this.scene, "resonantDropSoft");
    }
    this.particles.createPointEmitter(
      new Vector3(-1, -1, -1),
      new Vector3(1, 1, 1),
    );
    this.particles.emitter = epicenter.clone();
    this.particles.minLifeTime = 0.4;
    this.particles.maxLifeTime = 1.0;
    this.particles.minSize = 0.08;
    this.particles.maxSize = 0.25;
    this.particles.minEmitPower = 3;
    this.particles.maxEmitPower = 8;
    this.particles.color1 = a11yColor4('BRASS', COLORS.BRASS, 1.0, rc);
    this.particles.color2 = a11yColor4('EMERALD', COLORS.EMERALD, 1.0, rc);
    this.particles.colorDead = new Color4(0.3, 0.15, 0.05, 0);
    this.particles.gravity = new Vector3(0, -5, 0);
    this.particles.emitRate = 0;
    this.particles.manualEmitCount = manualCount;
    this.particles.blendMode = ParticleSystem.BLENDMODE_ADD;
    this.particles.start();

    // Emit event for scene to check blockage overlap — always uses the full
    // base radius so gameplay is not affected by the visual scale reduction.
    gameEvents.emit(GameEvents.RESONANT_DROP_TRIGGERED, {
      x: epicenter.x,
      y: epicenter.y,
      z: epicenter.z,
      radius: this.shockwaveMaxRadiusBase,
    });

    gameEvents.emit(GameEvents.COOP_ABILITY_USED, {
      abilityId: this.id,
    });

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) {
      // Tick cooldown
      if (this.cooldownRemaining > 0) {
        this.cooldownRemaining = Math.max(0, this.cooldownRemaining - deltaTime);
      }
      return;
    }

    this.shockwaveTimer += deltaTime;
    const progress = Math.min(this.shockwaveTimer / this.shockwaveDuration, 1);
    this.shockwaveRadius = this.shockwaveMaxRadius * progress;

    // Scale ring meshes outward; fade alpha
    const alpha = 1 - progress;
    for (let i = 0; i < this.ringMeshes.length; i++) {
      const ring = this.ringMeshes[i];
      const scaleFactor = this.shockwaveRadius * 2 * (1 - i * 0.15);
      if (scaleFactor <= 0) {
        ring.isVisible = false;
        continue;
      }
      ring.scaling.set(scaleFactor, scaleFactor, scaleFactor);
      const mat = ring.material as StandardMaterial;
      mat.alpha = alpha * (1 - i * 0.25) * (this.visualConfig.ringOpacity ?? 1);
    }

    // End shockwave
    if (progress >= 1) {
      this.deactivate();
    }
  }

  deactivate(): void {
    this.isActive = false;
    this.cooldownRemaining = this.cooldownDuration;

    // Dispose ring meshes
    for (const ring of this.ringMeshes) {
      ring.material?.dispose();
      ring.dispose();
    }
    this.ringMeshes = [];

    // Stop and dispose particles
    if (this.particles) {
      this.particles.stop();
      this.particles.dispose();
      this.particles = null;
    }
  }

  /** Current shockwave info for overlap checking. */
  getShockwave(): { position: Vector3; radius: number } | null {
    if (!this.isActive) return null;
    return {
      position: Vector3.Center(this.basilMesh.position, this.minnaMesh.position),
      radius: this.shockwaveRadius,
    };
  }

  dispose(): void {
    this.deactivate();
    if (this._a11yUnsub) {
      this._a11yUnsub();
      this._a11yUnsub = null;
    }
  }
}
